/*
Purpose: Loads and caches everything the Calendar (v2) screen reads.
*/

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  fetchDeferBin,
  fetchDeferRequests,
  fetchDisapprovedDeferRequests,
  fetchNotes,
  fetchOneOffRequests,
  fetchOperatingHours,
  fetchSessions,
  fetchTutorOptions,
} from './calendar-api';

const EMPTY_INBOXES = {
  deferRequests: [],
  deferBin: [],
  oneOffRequests: [],
  disapproved: [],
};

/**
 * @param {Object} params
 * @param {string} params.startDate Inclusive `YYYY-MM-DD` window start.
 * @param {string} params.endDate   Inclusive `YYYY-MM-DD` window end.
 * @param {string} params.hoursFilter One of `HOURS_FILTER`.
 */
export default function useCalendarData({ startDate, endDate, hoursFilter }) {
  const [rawSessions, setRawSessions] = useState([]);
  const [operatingHours, setOperatingHours] = useState(null);
  const [inboxes, setInboxes] = useState(EMPTY_INBOXES);
  const [notes, setNotes] = useState([]);
  const [tutorOptions, setTutorOptions] = useState([]);

  const [hasLoadedSessions, setHasLoadedSessions] = useState(false);
  const [isFetchingSessions, setIsFetchingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState(null);

  // Incremented per request; only the newest response is allowed to write state.
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadSessions = useCallback(async () => {
    if (!startDate || !endDate) {
      return;
    }

    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    setIsFetchingSessions(true);
    setSessionsError(null);

    try {
      const data = await fetchSessions({ startDate, endDate, hoursFilter });
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }
      setRawSessions(data);
    } catch (error) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }
      console.error('Calendar: unable to load sessions', error);
      setSessionsError('Unable to load the calendar. Please try again.');
      setRawSessions([]);
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setIsFetchingSessions(false);
        setHasLoadedSessions(true);
      }
    }
  }, [startDate, endDate, hoursFilter]);

  const loadInboxes = useCallback(async () => {
    try {
      const [deferRequests, deferBin, oneOffRequests, disapproved] = await Promise.all([
        fetchDeferRequests(),
        fetchDeferBin(),
        fetchOneOffRequests(),
        fetchDisapprovedDeferRequests(),
      ]);

      if (!isMountedRef.current) {
        return;
      }
      setInboxes({ deferRequests, deferBin, oneOffRequests, disapproved });
    } catch (error) {
      console.error('Calendar: unable to load request inboxes', error);
    }
  }, []);

  const loadNotes = useCallback(async () => {
    try {
      const data = await fetchNotes();
      if (isMountedRef.current) {
        setNotes(data);
      }
    } catch (error) {
      console.error('Calendar: unable to load notes', error);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    loadInboxes();
    loadNotes();
  }, [loadInboxes, loadNotes]);

  // Operating hours and the tutor list are centre configuration, so one fetch is enough.
  useEffect(() => {
    let isActive = true;

    Promise.all([fetchOperatingHours(), fetchTutorOptions()])
      .then(([hours, tutors]) => {
        if (!isActive) {
          return;
        }
        setOperatingHours(hours);
        setTutorOptions(tutors);
      })
      .catch((error) => {
        console.error('Calendar: unable to load centre configuration', error);
      });

    return () => {
      isActive = false;
    };
  }, []);

  /** Refresh everything a mutation could have changed. */
  const refreshAll = useCallback(async () => {
    await Promise.all([loadSessions(), loadInboxes()]);
  }, [loadSessions, loadInboxes]);

  /**
   * After createStudentPause succeeds, the list API sometimes still returns the prior
   * defer/attendance chip until the pause window fully applies. Stamp `is_paused` on
   * the matching enrolment rows so the drawer status/actions update immediately.
   * Does not call the server — payment/refund already ran inside createStudentPause.
   */
  const markStudentsPaused = useCallback(
    ({ enrolKey, studentKey, sessionTemplateKey, sessionDate, applyAll }) => {
      setRawSessions((previous) =>
        previous.map((event) => {
          if (!applyAll) {
            if (
              sessionTemplateKey != null &&
              String(event.fk_sest_key) !== String(sessionTemplateKey)
            ) {
              return event;
            }
            if (sessionDate && event.ses_date && String(event.ses_date) !== String(sessionDate)) {
              return event;
            }
          }

          const description = Array.isArray(event.description) ? event.description : [];
          let changed = false;
          const nextDescription = description.map((row) => {
            const matchesEnrol =
              enrolKey != null &&
              row.pk_enrol_key != null &&
              String(row.pk_enrol_key) === String(enrolKey);
            const matchesStudent =
              studentKey != null &&
              row.fk_stu_key != null &&
              String(row.fk_stu_key) === String(studentKey);
            if (!matchesEnrol && !matchesStudent) {
              return row;
            }
            if (parseInt(row.is_paused, 10) === 1 || row.is_paused === true) {
              return row;
            }
            changed = true;
            return { ...row, is_paused: 1 };
          });

          return changed ? { ...event, description: nextDescription } : event;
        })
      );
    },
    []
  );

  return {
    rawSessions,
    operatingHours,
    inboxes,
    notes,
    tutorOptions,
    // The grid renders a skeleton until the first response, then dims during refetches.
    isInitialLoading: !hasLoadedSessions,
    isRefreshing: hasLoadedSessions && isFetchingSessions,
    sessionsError,
    reloadSessions: loadSessions,
    reloadInboxes: loadInboxes,
    reloadNotes: loadNotes,
    refreshAll,
    markStudentsPaused,
  };
}
